/*
 * Copyright (C) 2024 Reseau Concept.
 * https://reseau-concept.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfigService, UserPreferencesService, UserPreferenceValues } from '@alfresco/adf-core';
import { CollaboraOnlineService } from '../services/collabora-online.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule],
  selector: 'collabora-online',
  templateUrl: './collabora-online.component.html',
  styleUrls: ['./collabora-online.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CollaboraOnlineComponent implements OnInit, OnDestroy {
  @ViewChild('form') postForm: ElementRef;
  @ViewChild('access_token') inputToken: ElementRef;
  @ViewChild('access_token_ttl') inputTokenTTL: ElementRef;
  @ViewChild('loleafletFrame') loleafletFrame: ElementRef;

  action: string;
  nodeId: string;
  previousUrl: string;
  accessToken: string;
  accessTokenTTL: string;
  iFrameUrl: string;
  listenerHandlePostMessage: any;
  locale: string;

  /** Origine du serveur Collabora, seule source de messages acceptee. */
  private wopiOrigin: string | null = null;

  /**
   * Message d'echec du chargement, si echec.
   *
   * POURQUOI CET ETAT EXISTE. `ngOnInit` etait un `async` sans `try` : le moindre appel en echec
   * rejetait la promesse sans que personne ne la rattrape. L'utilisateur voyait un cadre vide, ou
   * — pire — le formulaire partait quand meme avec `access_token=undefined`, et Collabora
   * repondait alors par son diagnostic a lui, `cmd=internal kind=unauthorized`, qui ne dit rien de
   * la cause reelle. Le probleme etait cote repository et restait invisible.
   */
  loadErrorKey: string | null = null;

  /** Detail technique de l'echec, affiche sous le message pour pouvoir etre rapporte. */
  loadErrorDetail: string | null = null;

  private readonly onDestroy$ = new Subject<boolean>();

  private readonly collaboraOnlineService = inject(CollaboraOnlineService);
  private readonly userPreferencesService = inject(UserPreferencesService);
  private readonly appConfig = inject(AppConfigService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  async ngOnInit() {
    this.action = this.route.snapshot.params['action'];
    this.nodeId = this.route.snapshot.params['nodeId'];

    this.userPreferencesService
      .select(UserPreferenceValues.Locale)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((locale) => (this.locale = locale));

    this.previousUrl = this.collaboraOnlineService.getPreviousUrl();

    try {
      await this.openEditor();
    } catch (error) {
      this.reportFailure(error);
    }
  }

  /**
   * Prepare et lance l'editeur.
   *
   * Isole de `ngOnInit` pour que le `try` de l'appelant couvre reellement tout le parcours : un
   * `await` en echec au milieu d'un `ngOnInit` de cinquante lignes laissait le composant a
   * mi-chemin, sans que rien ne le signale.
   */
  private async openEditor(): Promise<void> {
    // Les deux appels sont independants — l'URL du serveur Collabora et le jeton — et etaient
    // attendus l'un apres l'autre. Ils partent maintenant ensemble.
    //
    // Un troisieme appel les accompagnait, sur l'API des noeuds : il ne servait qu'a nommer le
    // fichier et a resoudre son icone dans notre barre d'outils. Celle-ci ayant disparu — Collabora
    // porte deja la sienne, avec fermeture, nom du fichier et plein ecran — l'appel n'a plus
    // d'objet, et l'ouverture economise un aller-retour.
    const [wopiHostUrl, token] = await Promise.all([
      this.collaboraOnlineService.getLoolUrl(),
      this.collaboraOnlineService.getAccessToken(this.nodeId, this.action)
    ]);

    // Le repository ne rend pas toujours `wopi_src_url` pour l'action demandee ; on retombe alors
    // sur celle d'edition.
    const usableToken = token?.wopi_src_url ? token : await this.collaboraOnlineService.getAccessToken(this.nodeId, 'edit');

    // GARDE INDISPENSABLE. Sans elle, un jeton absent partait tel quel : le formulaire postait la
    // chaine « undefined », que le repository ne retrouve evidemment pas dans son cache de jetons,
    // et Collabora concluait `kind=unauthorized`. Le diagnostic affiche etait donc celui de
    // l'editeur, pour une panne qui n'etait pas la sienne.
    if (!usableToken?.access_token || !usableToken.wopi_src_url) {
      this.loadErrorKey = 'COLLABORA.ERROR.NO_TOKEN';
      this.loadErrorDetail =
        'access_token=' + (usableToken?.access_token ?? '(absent)') + ' wopi_src_url=' + (usableToken?.wopi_src_url ?? '(absent)');
      return;
    }

    this.accessToken = usableToken.access_token;
    this.accessTokenTTL = usableToken.access_token_ttl;

    const wopiFileUrl = wopiHostUrl + 'wopi/files/' + this.nodeId;

    // `encodeURIComponent`, pas `encodeURI` : ce dernier laisse passer « : » et « / », si bien que
    // l'URL du fichier arrivait brute dans le parametre. Collabora le releve —
    // « WOPISrc validation error: unencoded WOPISrc » — et n'a aucune raison de continuer a le
    // tolerer.
    this.iFrameUrl = usableToken.wopi_src_url + 'WOPISrc=' + encodeURIComponent(wopiFileUrl) + '&lang=' + this.locale;

    if (this.action === 'view') {
      this.iFrameUrl += '&permission=readonly';
    }

    // Origine attendue des messages de l'iframe. Voir `handlePostMessage`.
    this.wopiOrigin = this.originOf(usableToken.wopi_src_url);

    // Remplissage du formulaire dynamique
    this.postForm.nativeElement.action = this.iFrameUrl;
    this.inputToken.nativeElement.value = this.accessToken;
    this.inputTokenTTL.nativeElement.value = this.accessTokenTTL;

    // Déclenchement du post
    this.postForm.nativeElement.submit();

    // Ajout de listener pour les évènement
    if (!this.listenerHandlePostMessage) {
      this.listenerHandlePostMessage = this.handlePostMessage.bind(this);
    }
    window.addEventListener('message', this.listenerHandlePostMessage, true);
  }

  /**
   * Traduit un echec de preparation en message affichable.
   *
   * On distingue le cas de la session, parce que c'est le seul que l'utilisateur peut corriger
   * lui-meme. Les webscripts `lool` et l'API des noeuds sont appeles avec un en-tete
   * `Authorization` construit a la main (voir `collabora-auth.ts`) : quand le jeton OIDC n'est plus
   * valide, le repository repond « Failed to extract username from token », en 401 comme en 500
   * selon le webscript. Les deux disent la meme chose.
   */
  private reportFailure(error: unknown): void {
    const status = (error as { status?: number })?.status;
    const body = (error as { error?: unknown })?.error;
    const detail = typeof body === 'string' ? body : JSON.stringify(body ?? (error as { message?: string })?.message ?? error);

    const sessionExpired = status === 401 || status === 403 || /extract username from token|decode the Jwt/i.test(detail || '');

    this.loadErrorKey = sessionExpired ? 'COLLABORA.ERROR.SESSION' : 'COLLABORA.ERROR.GENERIC';
    this.loadErrorDetail = [status ? 'HTTP ' + status : null, detail ? detail.slice(0, 400) : null].filter(Boolean).join(' \u2014 ');
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
    window.removeEventListener('message', this.listenerHandlePostMessage, true);
  }

  /** Origine d'une URL, ou `null` si elle n'est pas analysable. */
  private originOf(url: string): string | null {
    try {
      return new URL(url, window.location.origin).origin;
    } catch {
      return null;
    }
  }

  /**
   * Messages de l'iframe Collabora.
   *
   * Deux gardes indispensables, absentes jusqu'ici :
   *
   * - **l'origine**. L'ecouteur est pose sur `window` : il recoit TOUS les messages de la page,
   *   d'ou qu'ils viennent. Sans verification, n'importe quel cadre pouvait envoyer un `UI_Close`
   *   et faire quitter l'editeur. C'est aussi ce que demande le protocole WOPI.
   * - **le format**. `JSON.parse(event.data)` sans filet levait une exception sur tout message qui
   *   n'est pas du JSON — et il en circule (outils de developpement, extensions du navigateur,
   *   autres cadres). L'exception remontait dans l'ecouteur a chaque message etranger.
   */
  private handlePostMessage(event: MessageEvent): void {
    if (this.wopiOrigin && event.origin !== this.wopiOrigin) {
      return;
    }

    let message: any;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    if (message?.MessageId === 'UI_Close') {
      this.navigateBack();
      return;
    }

    // Collabora n'accepte les messages de l'hote qu'une fois le document charge, et signale ce
    // moment par `App_LoadingStatus`. C'est donc le seul point ou nos commandes peuvent partir.
    if (message?.MessageId === 'App_LoadingStatus' && message?.Values?.Status === 'Document_Loaded') {
      this.onDocumentLoaded();
    }
  }

  /**
   * Reglages appliques a l'ouverture, pour tous les utilisateurs.
   *
   * `Host_PostmessageReady` est la poignee de main prevue par le protocole : elle declare que
   * l'hote ecoute. Collabora l'ignore dans sa version actuelle, mais l'envoyer est ce que demande
   * la specification et ce qui protege d'un changement de leur cote.
   */
  private onDocumentLoaded(): void {
    this.postToEditor({ MessageId: 'Host_PostmessageReady' });

    if (this.appConfig.get<boolean>('collabora.trackChanges', true)) {
      // Le suivi des modifications — « Revision > Enregistrer les modifications » — est un reglage
      // du DOCUMENT, pas de l'application : il n'existe aucun equivalent global cote serveur. On
      // le pose donc a chaque ouverture.
      //
      // ATTENTION A LA FORME DE LA COMMANDE. `.uno:TrackChanges` seule est une BASCULE : envoyee
      // sur un document ou le suivi est deja actif, elle le desactiverait. Et l'hote n'a aucun
      // moyen de connaitre l'etat courant — Collabora n'emet pas de message d'etat de commande
      // (la liste de ses messages sortants ne contient rien de tel).
      //
      // La forme parametree leve l'ambiguite : elle FIXE l'etat au lieu de l'inverser. C'est la
      // meme que Collabora emploie dans ses propres menus, ou l'entree « Aucun utilisateur »
      // envoie `.uno:TrackChanges?TrackChanges:bool=false`.
      this.postToEditor({ MessageId: 'Send_UNO_Command', Values: { Command: '.uno:TrackChanges?TrackChanges:bool=true' } });
    }
  }

  /** Envoie un message a l'iframe, sur son origine et elle seule. */
  private postToEditor(message: Record<string, unknown>): void {
    const frame = this.loleafletFrame?.nativeElement as HTMLIFrameElement;
    if (frame?.contentWindow && this.wopiOrigin) {
      frame.contentWindow.postMessage(JSON.stringify(message), this.wopiOrigin);
    }
  }

  /**
   * Retour a la page precedente.
   *
   * `previousUrl` est vide lorsqu'on arrive directement sur `/collabora-online/...` — lien
   * partage, rechargement, nouvel onglet. `navigateByUrl(undefined)` levait alors une exception et
   * le bouton de fermeture ne faisait rien.
   */
  private navigateBack(): void {
    this.router.navigateByUrl(this.previousUrl || '/personal-files');
  }

  onBackButtonClick(): void {
    this.navigateBack();
  }
}
